import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState} from "react";
import { RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea, RCCTooltip, RCCButton } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import Audit from "src/components/Audit";
import Tabs from "src/components/Tabs";
import DateTimePicker from "src/components/DateTimePicker";
import LovUsers from "src/components/Lov/Users";
import { useAuth } from "src/context/Auth/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import Select from "src/components/Select";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import Permissions from "src/components/Permissions";
import LovRiskClassification from "src/components/Lov/RiskClassification";
import LovTags from "src/components/Lov/Tags";
import DeleteModal from "src/components/DeleteModal";
import LovProcess from "src/components/Lov/Process";
import LovSectors from "src/components/Lov/Sectors";
import Title from "src/components/Title";
import { simplePermissionType } from "src/components/Permissions/interface";
import { v4 } from "uuid";
import { ITypeModal } from "src/components/Modal/interface";
import { IRefIRisksModals,IComponentRisksPanelModal, IRisks, CheckQuadrant, CheckProbability, CheckImpact, RisksSituation } from "./interface";
import { addAndChangeSchema, deleteSchema } from "./schemas";
import ContingencePlans from "./components/ContingencePlan";
import { IContingencePlan } from "./components/ContingencePlan/interface";
import FormSimplifiedFields from "./components/FormSimplifiedFields";


const ModalsRisk: React.ForwardRefRenderFunction<IRefIRisksModals, IComponentRisksPanelModal> = ({
	onCallbackDelete,
	onCallbackUpdate
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const [ selectedRisk, setSelectedRisk ] = useState<IRisks>({} as IRisks);
	const [ isVisualize, setIsVisualize ] = useState(false);

	const { user } = useAuth();
	const { setLoading } = useLoaderBackdrop();
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState<boolean>(false);
	const [formSimplified, setFormSimplified] = useState(true);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const onCallbackAdd = useRef<Function | undefined>(undefined);

	const [contigencePlans, setContigencePlans] = useState<IContingencePlan[]>([]);

	// Modal delete
	const modalDelete = useRef<RCCRefModalProps>(null); 
	const { handleSubmit: handleSubmitDelete, register: registerDelete, reset : resetDelete, setError : setErrorDelete, formState: { errors : errorsDelete }, clearErrors: clearErrorsDelete } = useForm();


	const submitDelete = async (data: any) => {
		try{
			clearErrorsDelete();	
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "rib001TituloConfirmation": selectedRisk.rib001Titulo},{
				abortEarly: false
			});
			
			await RCCServicesApi.deleteRequest({
				method: "Risks/Delete",
				urlParameters: {
					id: selectedRisk.rib001Id,
					title: selectedRisk.rib001Titulo
				}
			}); 

			addToast({
				color: "green",
				description: translate("Risco excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();

			if(onCallbackDelete)
				onCallbackDelete(selectedRisk.rib001Id);

		}catch(err){
			modalDelete.current?.resetLoadingButton();

			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, "Erro ao excluir risco", addToast);
		}
		
	};

	const { handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange ,
		control: controlAddOrChange,
		unregister: unRegisterForm,
		watch: watchAddOrChange
	} = useForm();

	const objToInt = (obj: any) => {
		if(typeof obj === "object")
			return parseInt(obj.value, 10);
		if(typeof obj === "string")
			return parseInt(obj, 10);
		return obj;
	};


	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			const dataChanged = data;

			dataChanged.rib001Taa001Id = data.rib001Taa001Id ? objToInt(data.rib001Taa001Id) : null;
			dataChanged.rib001Taa001Id2 = data.rib001Taa001Id2 ? objToInt(data.rib001Taa001Id2) : null;
			dataChanged.rib001Taa005Id = data.rib001Taa005Id ? objToInt(data.rib001Taa005Id) : null;
			dataChanged.rib001Efa005Id = data.rib001Efa005Id ? objToInt(data.rib001Efa005Id) : "";

			

			if(data.ritbb007){
				data.ritbb007.forEach((oca006: any) => {	
					const oca006Changed = oca006;
					oca006Changed.rib007Efa004Id = oca006.rib007Efa004Id ? objToInt(oca006.rib007Efa004Id) : null;
					oca006Changed.rib007Efa005Id = oca006.rib007Efa005Id ? objToInt(oca006.rib007Efa005Id) : null;
					oca006Changed.rib007Permissao = oca006.rib007Permissao ? objToInt(oca006.rib007Permissao) : null;
					oca006Changed.rib007Id = objToInt(oca006.rib007Id);
					oca006Changed.hasOneOfTwoValues = !!(oca006Changed.rib007Efa004Id || oca006Changed.rib007Efa005Id);
				});
				dataChanged.ritbb007 = dataChanged.ritbb007.filter((x : any) => !(x.rib007Efa004Id == null && x.rib007Efa005Id == null));

				// verifica se tem permissão de Visializar e Editar
				if(!dataChanged.ritbb007.filter((y: any) => y.rib007Permissao.toString() === simplePermissionType.VIEW_AND_EDIT).length){
					modalAddOrChange.current?.resetLoadingButton();
					addToast({
						color: "yellow",
						description: translate("Ao menos uma permissão deve ser (Visualizar e Editar)"),
						title: translate("Atenção")
					});
					return;
				}
			}else{
				dataChanged.ritbb007 = permissionDefault();
			}			

			if(data.ritbb005){
				data.ritbb005.forEach((ritbb005: any) => {
					if(ritbb005.ritbb006){
						ritbb005.ritbb006.forEach((ritbb006: any) => {
							const ritbb006Changed = ritbb006;
							ritbb006Changed.rib006Dias = parseInt(ritbb006.rib006Dias, 10);
							ritbb006Changed.rib006Efa005Id = ritbb006.rib006Efa005Id ?  objToInt(ritbb006.rib006Efa005Id): "";
						});
					}
				});
			}

			await addAndChangeSchema(translate).validate(data,{
				abortEarly: false
			});

			let response = null;
			if(isAdd){
				response = await RCCServicesApi.postRequest({
					method: "Risks/Insert",
					body: {
						...data,
						rib001Data: data.rib001Data,
						rib001Probabilidade:  parseInt(data.rib001Probabilidade, 10),
						rib001Efa005Id: data.rib001Efa005Id,
						rib001Impacto: parseInt(data.rib001Impacto, 10),
						rib001Quadrante: parseInt(data.rib001Quadrante, 10),
						rib001Situacao: parseInt(data.rib001Situacao, 10),
						rib001Taa005Id: data.rib001Taa005Id,
						rib001Ria001Id: parseInt(data.rib001Ria001Id, 10),
						rib001Tags: data.rib001Tags? extractSingleValueFromArrayOfObject("value", data.rib001Tags): null
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Risks/Update",
					body: {
						...data,
						rib001Id: selectedRisk.rib001Id,
						rib001Ria001Id: parseInt(data.rib001Ria001Id, 10),
						rib001Data: data.rib001Data,
						rib001Probabilidade:  parseInt(data.rib001Probabilidade, 10),
						rib001Efa005Id: data.rib001Efa005Id,
						rib001Impacto: parseInt(data.rib001Impacto, 10),
						rib001Quadrante: parseInt(data.rib001Quadrante, 10),
						rib001Situacao: parseInt(data.rib001Situacao, 10),
						rib001Taa005Id: data.rib001Taa005Id,
						rib001Tags: data.rib001Tags? extractSingleValueFromArrayOfObject("value", data.rib001Tags): null
					}
				});	
			}

			if(isAdd){
				if(onCallbackAdd.current){
					if(response)
						onCallbackAdd.current(response.data);
					else
						onCallbackAdd.current();
				}		
			}else if(onCallbackUpdate){
				onCallbackUpdate(selectedRisk.rib001Id);
			}
			
			addToast({
				color: "green",
				description: isAdd ? translate("Risco adicionado com sucesso") : translate("Risco alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar risco") : translate("Erro ao alterar risco"), addToast);
			clearErrorsAddOrChange();
		}
	};

	const actionButtonAdd = (onCallback?: Function) => {
		setIsAdd(true); 
		setSelectedRisk({} as IRisks); 
		resetAddOrChange(); 
		setFormSimplified(true);
		setLoading(false);
		onCallbackAdd.current = onCallback;
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IRisks, finaly: boolean) => {
		setIsAdd(false); 
		setIsVisualize(finaly);
		resetAddOrChange(); 
		setFormSimplified(false);
		setLoading(true);	
		getSelectedRisk(row.rib001Id);	
	};	

	const getSelectedRisk = async (id: number) => {
		try{
			const risk = await RCCServicesApi.getRequest({
				method: "Risks/Select",
				urlParameters: {
					id
				}
			}); 
	
			setSelectedRisk(risk.data);
			setContigencePlans(risk.data.ritbb005);
			setLoading(false);
	

			modalAddOrChange.current?.openModal(); 

		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar o risco"), addToast);
			setLoading(false);
		}
	};
	
	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const actionButtonDelete = (row : IRisks) => { 
		resetDelete();
		setSelectedRisk(row); 
		modalDelete.current?.openModal();
	};

	const permissionDefault = () => [{
		id: v4(),
		rib007Id: 0,
		rib007Rib001Id: 0,
		rib007Efa004Id: null,
		rib007Efa004Descricao: null,
		rib007Efa005Id : user.efa005Id,
		rib007Efa005Nome: user.efa005Nome,
		rib007Permissao : simplePermissionType.VIEW_AND_EDIT
	}];

	const PermissionsComponent = useMemo(() => (
		<>
			<Permissions
				register={registerAddOrChange}
				unregister={unRegisterForm}
				control={controlAddOrChange}
				error={errorsAddOrChange}
				disabled={isVisualize}
				clearErrors={clearErrorsAddOrChange}
				permissionsNames= {{
					nameObject: "ritbb007",
					nameOfID: "rib007Id",
					nameOfSelectUserDescription: "rib007Efa005Nome",
					nameOfSelectGroupDescription: "rib007Efa004Descricao",
					nameOfSelectUser: "rib007Efa005Id",
					nameOfSelectGroup: "rib007Efa004Id",
					nameOfSelectPermission: "rib007Permissao",
					defaultValue: isAdd ? permissionDefault() : selectedRisk.ritbb007
				}}
			/>
		</>
	), [selectedRisk.ritbb007, errorsAddOrChange.ritbb007, isAdd]);

	const modalTitle = () => {
		if(isAdd) 
			return translate("Adicionar risco");
		if(isVisualize)
			return translate("Visualizar risco");
		return translate("Alterar risco");
	};

	const modalType = () => {
		if(isAdd) 
			return ITypeModal.ADD;
		if(isVisualize)
			return ITypeModal.VIEW;
		return ITypeModal.CHANGE;
	};

	return (
		<> 
			{/* Modal add/change */}
			<Modal
				type={modalType()}
				ref={modalAddOrChange}
				header={{
					title: modalTitle(),
					icon: {
						name: isVisualize ? "eye": "infoCircle"
					},
					subtitle: !isAdd ? `${translate("Risco")}: ${selectedRisk.rib001Titulo}` : undefined
				}}
				withoutForm
				footer={{
					showSaveButton: !isVisualize,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar"),
					childrenOfStandardFooter: <>{isAdd && (
						<RCCButton color="blue" size="sm"
							onClick={() => {
								const rib001Titulo = watchAddOrChange("rib001Titulo");
								const rib001Data = watchAddOrChange("rib001Data");
								const rib001Situacao = watchAddOrChange("rib001Situacao");
								const rib001Probabilidade = watchAddOrChange("rib001Probabilidade");
								const rib001Efa005Id = watchAddOrChange("rib001Efa005Id");
								const rib001Quadrante = watchAddOrChange("rib001Quadrante");
								const rib001Ria001Id = watchAddOrChange("rib001Ria001Id");
								const rib001Impacto = watchAddOrChange("rib001Impacto");
								setSelectedRisk((prevState) => ({
									...prevState,
									rib001Titulo,
									rib001Data,
									rib001Situacao,
									rib001Efa005Id,
									rib001Quadrante,
									rib001Ria001Id,
									rib001Probabilidade,
									rib001Impacto			
								}));
								setFormSimplified((prevState) => !prevState);
							}}
						>
							{formSimplified ? translate("Ver cadastro completo") : translate("Ver cadastro simplificado")}
						</RCCButton>
					)}	
					</>
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				{formSimplified ? (
					<>
						<FormSimplifiedFields 
							controlAddOrChange={controlAddOrChange}
							errorsAddOrChange={errorsAddOrChange}
							isAdd={isAdd}
							registerAddOrChange={registerAddOrChange}
							selectedRisk={selectedRisk}
						/>
					</>
				): (
					<Tabs 
						tabInit={0}
						name="tabsRisks"
						styleInline="width: 100%;"
						options={[
							{
								label: translate("Geral"),
								content: (
									<RCCGrid
										container
										item
										direction="column"
										alignItems="center"
									>
										<RCCGrid
											container
											item
											xs={12}
										>
											<RCCGrid
												container
												item
												justifyContent="flex-end"
											>
												{
													!isAdd &&
													<Title color="purple">
														{translate("Código")}: {selectedRisk.rib001Codigo}
													</Title>
												}
												
												<Audit data={selectedRisk} type="pop"/>
											</RCCGrid>
											<RCCGrid
												container
												item
											>
												<RCCInput 
													label={translate("Título")} 
													name="rib001Titulo" 
													defaultValue={selectedRisk.rib001Titulo} 
													error={errorsAddOrChange.rib001Titulo}
													register={registerAddOrChange}
													maxLength={60}
													disabled={isVisualize}
													required
												/>
											</RCCGrid>
											<RCCGrid
												container
												item
											>
												<RCCTextArea 
													name="rib001Descricao"
													label={translate("Descrição do risco")} 
													maxLength={2000}
													register={registerAddOrChange}
													styleInline=">textarea{ max-height: 200px !important;} "
													defaultValue={selectedRisk.rib001Descricao}
													error={errorsAddOrChange.rib001Descricao}
													disabled={isVisualize}
												/>
											</RCCGrid>
											<RCCGrid
												container
												item
											>
												<RCCTextArea 
													name="rib001Causa"
													label={translate("Causa")} 
													maxLength={2000}
													register={registerAddOrChange}
													styleInline=">textarea{ max-height: 200px !important;} "
													defaultValue={selectedRisk.rib001Causa}
													error={errorsAddOrChange.rib001Causa}
													disabled={isVisualize}
												/>
											</RCCGrid>
											<RCCGrid
												container
												item
												sm={12}
												alignItems="center"
											>
												<RCCGrid
													container
													item
													xs={12}
													sm={6}
													styleInline="@media (min-width: 600px){padding-right: 4px;}"
												>
													<DateTimePicker 
														type="datePicker"
														label={translate("Data")}
														name="rib001Data"
														control={controlAddOrChange}
														defaultValue={selectedRisk.rib001Data ? selectedRisk.rib001Data : new Date()}
														error={errorsAddOrChange.rib001Data}
														props={{
															datePicker: {
																disabled: isVisualize
															}
														}}
														required
													/>
												</RCCGrid>
												<RCCGrid
													container
													item
													xs={12}
													sm={6}
													styleInline="@media (min-width: 600px){padding-left: 4px;}"
												>
													<Select 
														control={controlAddOrChange}
														name="rib001Situacao" 
														options={[	
															{
																value: RisksSituation.OPEN.toString(),
																label: translate("Aberto")
															},{
																value: RisksSituation.FINALY.toString(),
																label: translate("Finalizado")
															},
															{
																value: RisksSituation.CANCELED.toString(),
																label: translate("Cancelado")
															}
														]} 
														label={translate("Situação")}
														defaultValue={selectedRisk.rib001Situacao ? selectedRisk.rib001Situacao.toString() : `${RisksSituation.OPEN}`}
														error={errorsAddOrChange.rib001Situacao}
														disabled={isAdd || isVisualize}
														required
													/>
												</RCCGrid>										
											</RCCGrid>
											<RCCGrid 
												container
												item
												xs={12}
												direction="row"
												justifyContent="space-between"
											>
												<LovUsers 
													control={controlAddOrChange}
													name="rib001Efa005Id"
													label={translate("Responsável")}
													defaultValue={selectedRisk.eftba005 ? {
														value: selectedRisk.rib001Efa005Id,
														label:  selectedRisk.eftba005.efa005Nome
													}: selectedRisk.rib001Efa005Id ?? `${user.efa005Id}`}
													error={errorsAddOrChange.rib001Efa005Id}
													disabled={isVisualize}
													required
												/>
	
											
											</RCCGrid>
	
											<RCCGrid 
												container
												item
												xs={12}
												direction="row"
												justifyContent="space-between"
											>
												<RCCGrid
													container
													item
													xs={12}
													sm={6}
													styleInline="@media (min-width: 600px){padding-right: 4px;}"
												>
													<RCCGrid
														container
														item	
													>
														<Select 
															control={controlAddOrChange}
															name="rib001Quadrante" 
															options={[{
																value: CheckQuadrant.OPPORTUNITY.toString(),
																label: translate("Oportunidade")
															}, {
																value: CheckQuadrant.THREAT.toString(),
																label: translate("Ameaça")
															}]} 
															label={translate("Tipo")}
															defaultValue={selectedRisk.rib001Quadrante ? selectedRisk.rib001Quadrante.toString() :CheckQuadrant.OPPORTUNITY.toString()}
															error={errorsAddOrChange.rib001Quadrante}
															disabled={isVisualize}
															required
														/>
													</RCCGrid>	
			
												</RCCGrid>
												<RCCGrid
													container
													item
													xs={12}
													sm={6}
													styleInline="@media (min-width: 600px){padding-left: 4px;}"
												>
			
													<LovRiskClassification 
														control={controlAddOrChange}
														name="rib001Ria001Id" 
														label={translate("Classificação")}
														defaultValue={selectedRisk.rib001Ria001Id ? selectedRisk.rib001Ria001Id.toString() : ""}
														error={errorsAddOrChange.rib001Ria001Id}
														disabled={isVisualize}
														isCreatable
														required
													/>
												</RCCGrid>	
											</RCCGrid>	
												
											<RCCGrid 
												container
												item
												xs={12}
												direction="row"
												justifyContent="space-between"
											>
												<RCCGrid
													container
													item
													xs={12}
													sm={6}
													alignItems="baseline"
													justifyContent="space-between"
												>
	
													<RCCGrid
														container
														item
														styleInline="@media (min-width: 600px){padding-right: 4px;}"
														style={{width: isAdd || isVisualize ? "" : "calc(100% - 30px)"}}
													>	
														<Select 
															control={controlAddOrChange}
															name="rib001Probabilidade" 
															options={[
																{
																	value: CheckProbability.VERY_HIGH.toString(),
																	label: translate("Muito alto")
																},{
																	value: CheckProbability.HIGH.toString(),
																	label: translate("Alto")
																},
																{
																	value: CheckProbability.MODERATE.toString(),
																	label: translate("Moderado")
																},
																{
																	value: CheckProbability.LOW.toString(),
																	label: translate("Baixo")
																},{
																	value: CheckProbability.VERY_LOW.toString(),
																	label: translate("Muito baixo")
																}
															]} 
															label={translate("Probabilidade")}
															error={errorsAddOrChange.rib001Probabilidade}
															defaultValue={selectedRisk.rib001Probabilidade ? selectedRisk.rib001Probabilidade.toString() : CheckProbability.MODERATE.toString()}
															submit="value"
															required
															disabled={!isAdd || isVisualize}
														/>
	
													</RCCGrid>
	
													<RCCGrid
														container
														item
														justifyContent="center"
														styleInline="@media (min-width: 600px){padding-right: 4px;}"
														style={{ width: isAdd  ? "" : "30px" }}
													>
														{(!isAdd && !isVisualize) && (
													
															<RCCTooltip
																color="grey"
																text={translate("Alteração permitida apenas na análise de risco")}
															>
																<RCCIcon 
																
																	name="infoCircle"
																/>
															</RCCTooltip>
														)}
													</RCCGrid>
												
												
												</RCCGrid>
												<RCCGrid
													container	
													item
													xs={12}
													sm={6}
													styleInline="@media (min-width: 600px){padding-left: 4px;}"
													alignItems="baseline"
												>
													<RCCGrid
														container
														item
														justifyContent="center"
														style={{width: isAdd || isVisualize ? "" : "calc(100% - 30px)"}}
													>
														<Select 
															control={controlAddOrChange}
															name="rib001Impacto" 
															options={[	
																{
																	value: CheckImpact.VERY_HIGH.toString(),
																	label: translate("Muito alto")
																},{
																	value: CheckImpact.HIGH.toString(),
																	label: translate("Alto")
																},
																{
																	value: CheckImpact.MODERATE.toString(),
																	label: translate("Moderado")
																},
																{
																	value: CheckImpact.LOW.toString(),
																	label: translate("Baixo")
																},{
																	value: CheckImpact.VERY_LOW.toString(),
																	label: translate("Muito baixo")
																}
															]} 
															label={translate("Impacto")}
															defaultValue={selectedRisk.rib001Impacto ? selectedRisk.rib001Impacto.toString() : CheckImpact.MODERATE.toString()}
															required
															error={errorsAddOrChange.rib001Impacto}
															disabled={!isAdd || isVisualize}
														/>
	
													</RCCGrid>
													<RCCGrid
														container
														item
														justifyContent="center"
														styleInline="@media (min-width: 600px){padding-right: 4px;}"
														style={{ width: isAdd  ? "" : "30px" }}
													>
														{(!isAdd && !isVisualize) && (
													
															<RCCTooltip
																color="grey"
																text={translate("Alteração permitida apenas na análise de risco")}
															>
																<RCCIcon 
																
																	name="infoCircle"
																/>
															</RCCTooltip>
														)}
													</RCCGrid>
												
												
											
												</RCCGrid>
											</RCCGrid>	
											<RCCGrid 
												container
												item
												xs={12}
												direction="row"
												justifyContent="space-between"
											>
												<RCCGrid
													container
													item
													xs={12}
													sm={6}
													styleInline="@media (min-width: 600px){padding-left: 4px;}"	
												>
													<LovProcess 
														control={controlAddOrChange}
														name="rib001Taa005Id" 
														label={translate("Processo")}
														defaultValue={selectedRisk.processo ? { value: selectedRisk.rib001Taa005Id.toString(), label: selectedRisk.processo }: 	""}
														error={errorsAddOrChange.rib001Taa005Id}
														disabled={isVisualize}
														isClearable
														isCreatable={false}
													/>
												</RCCGrid>
												<RCCGrid
													container
													item
													xs={12}
													sm={6}
													styleInline="@media (min-width: 600px){padding-left: 4px;}"
												>
													<LovSectors
														name="rib001Taa001Id"
														label={translate("Setor do risco")}
														control={controlAddOrChange}
														error={errorsAddOrChange.rib001Taa001Id}
														isCreatable
														defaultValue={selectedRisk.rib001Taa001Id?.toString()}
														isClearable
													/>
												</RCCGrid>	
											</RCCGrid>
											<RCCGrid
												container
												item
											>
												<LovTags 
													control={controlAddOrChange}
													name="rib001Tags"
													label={translate("Tags")}
													defaultValue={Array.isArray(selectedRisk.rib001Tags) ? selectedRisk.rib001Tags.join(", ") : selectedRisk.rib001Tags}
													disabled={isVisualize}
													extraParametersOnLoad={{
														use: 6
													}}
													extraParametersOnCreate={{
														use: 6
													}}
												/>
											</RCCGrid>
										</RCCGrid>		
									</RCCGrid>	
								)
							},
							{
								label: translate("Impacto"),
								content: (
									<>
										<RCCGrid 
											container
											item
											xs={12}
											direction="row"
											justifyContent="space-between"
										>
											<RCCGrid
												container
												item
												xs={12}
												sm={6}
												styleInline="@media (min-width: 600px){padding-left: 4px;}"
											>
												<LovSectors
													name="rib001Taa001Id2"
													label={translate("Setor do impacto")}
													control={controlAddOrChange}
													error={errorsAddOrChange.rib001Taa001Id2}
													isCreatable
													defaultValue={selectedRisk.rib001Taa001Id2?.toString()}
													isClearable
												/>
											</RCCGrid>	
										</RCCGrid>	
										<RCCGrid
											container
											item	
										>
											<RCCTextArea 
												name="rib001Descimpacto"
												label={translate("Descrição do impacto")} 
												maxLength={400}
												register={registerAddOrChange}
												styleInline=">textarea{ max-height: 200px !important;} "
												defaultValue={selectedRisk.rib001Descimpacto}
												error={errorsAddOrChange.rib001Descimpacto}
												disabled={isVisualize}
											/>
										</RCCGrid>
										<RCCGrid
											container
											item	
										>
											<RCCTextArea 
												name="rib001Prevencao"
												label={translate("Prevenção")} 
												maxLength={400}
												register={registerAddOrChange}
												styleInline=">textarea{ max-height: 200px !important;} "
												defaultValue={selectedRisk.rib001Prevencao}
												error={errorsAddOrChange.rib001Prevencao}
												disabled={isVisualize}
											/>
										</RCCGrid>
										<RCCGrid
											container
											item	
										>
											<RCCInput 
												label={translate("Local")} 
												name="rib001Local" 
												defaultValue={selectedRisk.rib001Local} 
												error={errorsAddOrChange.rib001Local}
												register={registerAddOrChange}
												maxLength={100}
												disabled={isVisualize}
											/>
										</RCCGrid>
									</>
								)
							},
							{
								label: translate("Planos de contigência"),
								content: (
									<ContingencePlans 
										clearErrors={clearErrorsAddOrChange}
										defaultValue={contigencePlans}
										control={controlAddOrChange}
										error={errorsAddOrChange}
										register={registerAddOrChange}
										unregister={unRegisterForm}		
										disabled={isVisualize}
									/>
								)
							},
							{
								label: translate("Permissões"),
								content: (
									<>
										{PermissionsComponent}
									</>
								) 
							}
						]}
					/>
				)}
				
			</Modal>
		  {/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de Risco")}
				description= {`${translate("Deseja realmente excluir o risco")}: ${selectedRisk.rib001Titulo}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
				withoutForm
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="rib001Titulo"
						label={translate("Digite o título do risco para excluí-lo")}
						maxLength={200}
						error={errorsDelete.rib001Titulo}
						required
					/>
				</RCCGrid>
			</DeleteModal>
		</>
	);	
};

export default forwardRef(ModalsRisk);
